import React from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { useMemo, useCallback } from 'react'
import { RadioButtonUnchecked, RadioButtonChecked, CheckBox, CheckBoxOutlineBlank, Add } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, TextField, SxProps, Divider, useTheme } from '@mui/material';
import { Option } from '@/interfaces/form';

// Add this interface
interface OptionGroup {
	title: string;
	options: Option[];
}

type Props<T extends FieldValues> = {
	name: Path<T>;
	options?: Option[] | OptionGroup[];  // Update to support both formats
	label: string;
	createNew?: boolean;
	multiple?: boolean;
	onBlur?: () => void;
	onValueChange?: (value: any) => void;
	sx?: SxProps;
	required?: boolean;
	onCreateNew?: () => void;
	size?: "small" | "medium";
	grouped?: boolean;  // Add this to indicate grouped options
};

export function RHFAutocomplete<T extends FieldValues>({
	name,
	options = [],
	label,
	createNew = false,
	multiple = true,
	onValueChange,
	sx,
	required = false,
	onCreateNew,
	size = "medium",
	grouped = false,  // Default to false for backward compatibility
}: Props<T>) {
	const { control } = useFormContext();
	const theme = useTheme();

	// Memoize the checkbox icons to prevent unnecessary re-renders
	const icon = useMemo(() => multiple ? <CheckBoxOutlineBlank /> : <RadioButtonUnchecked />, []);
	const checkedIcon = useMemo(() => multiple ? <CheckBox /> : <RadioButtonChecked />, []);
	
	// Create New option constant
	const CREATE_NEW_OPTION: Option = useMemo(() => ({ label: 'Create New', value: '__create_new__' }), []);

	// Function to flatten grouped options for internal processing
	const flattenedOptions = useMemo(() => {
		if (!grouped) return options as Option[];
		
		// Get all options from all groups and deduplicate by value
		const allOptions = (options as OptionGroup[]).flatMap(group => group.options);
		
		// Deduplicate options by value
		const uniqueValues = new Set();
		return allOptions.filter(option => {
			const val = option.value.toString();
			if (uniqueValues.has(val)) {
				return false; // Skip duplicate
			}
			uniqueValues.add(val);
			return true;
		});
	}, [options, grouped]);

	// Memoize the filter options function
	const filterOptions = useCallback((currentOptions: Option[], params: { inputValue: string }) => {
		const { inputValue } = params;
		
		// If onCreateNew is provided, we need to filter out the CREATE_NEW_OPTION first
		// to avoid duplicating it in the filtered results
		const optionsWithoutCreateNew = onCreateNew 
			? currentOptions.filter(opt => opt.value !== CREATE_NEW_OPTION.value)
			: currentOptions;

		const filteredOptions = optionsWithoutCreateNew.filter((option) =>
			option.label?.toLowerCase().includes(inputValue.toLowerCase())
		);
		
		// Add Create New option at the top if onCreateNew is provided
		// and if the input matches it or is empty
		const createNewOption = onCreateNew && 
			(inputValue === '' || '+ create new'.includes(inputValue.toLowerCase()))
			? [CREATE_NEW_OPTION] 
			: [];

		// Only add the new custom option if it doesn't exist and there's input
		if (createNew && inputValue && !optionsWithoutCreateNew.some(
			(option) =>
				option.label.toLowerCase() === inputValue.toLowerCase() ||
				option.value.toString().toLowerCase() === inputValue.toLowerCase()
		) && inputValue !== '+ Create New') {
			return [...createNewOption, ...filteredOptions, { label: inputValue, value: inputValue }];
		}

		return [...createNewOption, ...filteredOptions];
	}, [createNew, onCreateNew, CREATE_NEW_OPTION]);

	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { value, onChange, ref }, fieldState: { error } }) => {
				// Create a combined list of options including custom values
				const customOptions = (Array.isArray(value) ? value : [])
					.filter(val => !flattenedOptions.some(opt => opt.value === val))
					.map(val => ({ label: String(val), value: val }));

				// Add the CREATE_NEW_OPTION if onCreateNew is provided and deduplicate
				let baseOptions = onCreateNew 
					? [CREATE_NEW_OPTION, ...flattenedOptions]
					: flattenedOptions;

				// Ensure no duplicates in baseOptions
				baseOptions = baseOptions.filter((option, index, self) => 
					index === self.findIndex(o => o.value.toString() === option.value.toString())
				);

				const allOptions = [...baseOptions, ...customOptions];

				// Handle both existing and custom values
				let selectedOptions;
				
				if (multiple) {
					selectedOptions = Array.isArray(value) 
						? value.map((val:string) => allOptions.find((item) => 
							item.value === val || item.value?.toString() === val?.toString()
						)).filter(Boolean) 
						: [];
				} else {
					// For single select, try to find a match by value or by string comparison
					selectedOptions = value 
						? allOptions.find((item) => 
							item.value === value || 
							item.value.toString() === value.toString() ||
							item.value.toString().toLowerCase() === value.toString().toLowerCase()
						) 
						: null;
				}

				return (
					<Autocomplete
						multiple={multiple}
						size={size}
						options={grouped ? flattenedOptions : allOptions}
						groupBy={grouped ? (option) => {
							// Find which group this option belongs to
							const group = (options as OptionGroup[]).find(g => 
								g.options.some(opt => opt.value === option.value)
							);
							return group ? group.title : '';
						} : undefined}
						value={selectedOptions}
						getOptionLabel={(option) =>
							typeof option === 'string' ? option : option?.label || ''
						}
						isOptionEqualToValue={(option, selectedValue) => {
							if (!selectedValue) return false;
							// Ensure both option and selectedValue are objects
							if (typeof selectedValue === 'object' && selectedValue !== null) {
								return option.value === selectedValue.value || 
									option.value.toString() === selectedValue.value.toString();
							}
							return false;
						}}																								  
						onChange={(_, newValue) => {
							// Handle Create New option selection
							if (!multiple && 
								typeof newValue === 'object' && 
								newValue !== null &&
								!Array.isArray(newValue) && 
								'value' in newValue && 
								newValue.value === CREATE_NEW_OPTION.value) {
								onCreateNew?.();
								return;
							}
							
							if (multiple && Array.isArray(newValue)) {
								// Check if CREATE_NEW_OPTION is in the selection
								const hasCreateNew = newValue.some(item => 
									typeof item === 'object' && 
									item !== null && 
									'value' in item && 
									item.value === CREATE_NEW_OPTION.value
								);
								
								if (hasCreateNew) {
									onCreateNew?.();
									// Remove CREATE_NEW_OPTION from selection
									const filteredValues = newValue
										.filter(item => 
											!(typeof item === 'object' && 
											item !== null && 
											'value' in item && 
											item.value === CREATE_NEW_OPTION.value)
										)
										.map(item => typeof item === 'string' ? item : item?.value);
									
									onChange(filteredValues);
									onValueChange?.(filteredValues);
									return;
								}
							}
							
							const selectedValues = Array.isArray(newValue)
								? newValue.map((item) => typeof item === 'string' ? item : item?.value)
								: typeof newValue === 'string' ? newValue : newValue?.value;
							
							onChange(selectedValues);
							onValueChange?.(selectedValues);
						}}						  
						filterOptions={filterOptions}
						selectOnFocus
						clearOnBlur
						handleHomeEndKeys
						freeSolo={createNew}
						disableCloseOnSelect={multiple}
						onInputChange={(_, newInputValue, reason) => {
							if (createNew && reason === 'selectOption' && newInputValue) {
								// Immediately add the new value when Enter is pressed
								const newValues = [...(Array.isArray(value) ? value : []), newInputValue];
								onChange(newValues);
							}
						}}
						sx={sx}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								inputRef={ref}
								error={!!error}
								helperText={error?.message}
								label={label}
								required={required}
								size={size}
								slotProps={{
									htmlInput: {
										...params.inputProps,
										autoComplete: "no-name",
										sx: {
											...(size === "small" && {
												height: 'auto',
												padding: '0',
											})
										}
									},
									inputLabel: {
										sx: {
											'& .MuiFormLabel-asterisk': {
												color: 'error.main',
												fontSize: '24px',
											},
										},
									},
								}}
							/>
						)}
						renderOption={(props, option, { selected }) => {
							// Special handling for Create New option
							if (option.value === CREATE_NEW_OPTION.value && onCreateNew) {
								return (
									<React.Fragment key={option.value}>
										<Box 
											component="li" 
											{...props} 
											sx={{ 
												color: theme.palette.mode === 'dark' ? 'grey.200' : 'grey.900',
												fontWeight: 'bold',
												display: 'flex',
												alignItems: 'center'
											}}
											onClick={(e) => {
												e.preventDefault();
												onCreateNew();
											}}
										>
											<Add sx={{ mr: 1 }} />
											{option.label}
										</Box>
										<Divider />
									</React.Fragment>
								);
							}
							
							return (
								<Box component="li" {...props} key={option.value}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										checked={selected}
									/>
									{option.label}
								</Box>
							);
						}}
						renderGroup={(params) => (
							<li key={params.key}>
								<div style={{ 
									fontWeight: 'bold', 
									padding: '8px 16px', 
									backgroundColor: theme.palette.mode === 'dark' ? 
										theme.palette.grey[800] : theme.palette.grey[200]
								}}>
									{params.group}
								</div>
								<ul style={{ padding: 0 }}>{params.children}</ul>
							</li>
						)}
					/>
				);
			}}
		/>
	);
}
